import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import type { RetryCountdown as Countdown } from './useRetryCountdown';

/**
 * The Retry-After countdown's two parts (TMMAPP-0081, TMM-B3): the visible number, which ticks
 * every second and is hidden from assistive technology, and a visually hidden polite live region
 * that holds exactly two texts — "Try again in N s" while the delay runs (N fixed) and "You can
 * try again now" once Retry is available — so a screen reader hears it twice, not every second.
 * Renders nothing when the error carries no delay.
 *
 * The region is rendered EMPTY and its text written after mount: a live region inserted already
 * holding text is generally not announced (only `role="alert"` content is), so the start
 * announcement is a change to an existing region. The text is written into the node directly —
 * it is announcement plumbing, not rendered state.
 */
export function RetryCountdown({ delay, remaining }: Countdown) {
  const { t } = useTranslation();
  const region = useRef<HTMLSpanElement>(null);
  const text =
    delay === null
      ? ''
      : remaining > 0
        ? t('errors.retryIn', { seconds: delay })
        : t('errors.retryNow');
  useEffect(() => {
    if (region.current !== null) region.current.textContent = text;
  }, [text]);
  if (delay === null) return null;
  return (
    <>
      {remaining > 0 && (
        <Typography variant="body2" aria-hidden>
          {t('errors.retryIn', { seconds: remaining })}
        </Typography>
      )}
      <Box component="span" role="status" aria-live="polite" ref={region} sx={visuallyHidden} />
    </>
  );
}
